/**
 * Vista: Unidades (antes «Tractores») — ERP F1, B3 · D1 y D10
 *
 * La tabla central guarda cualquier producto serializado: tractor, implemento, dron, vehículo o maquinaria ligera.
 * Patrón de 2 pestañas (decisión de Germán, 18-oct):
 *   · «Consultar»: resumen, filtros (tipo, estado, origen, almacén, GPS) y listado. La ficha (👁) muestra producto, marca,
 *     pedimento, GPS y la línea de tiempo de la unidad.
 *   · «Agregar / editar»: la ficha editable. No hay «+ Agregar»: la unidad ENTRA por Recepciones (o llegó del GPS),
 *     para que no haya dos puertas de alta. Sólo aparece con permiso de editar; el candado real está en el servidor.
 * /tractores sigue llevando aquí (alias).
 */

import unidadesSvc, { TIPOS, ESTADOS, ORIGENES, NOMBRE_TIPO, NOMBRE_ESTADO, NOMBRE_ORIGEN, COLOR_ESTADO } from '../services/unidades.service.js';
import catalogos from '../services/erpCatalogos.service.js';
import almacenService from '../services/almacen.service.js';
import tractorService from '../services/tractor.service.js';
import permisos from '../services/permissions.service.js';
import router from '../router/router.js';
import modal from '../utils/modal.js';
import toast from '../utils/toast.js';
import { esc, errMsg, badge, val } from './catalogos/erpUi.js';
import { permisosPantalla, pestanasHtml, Pestanas } from '../utils/pestanasErp.js';

const opciones = (pares, vacio) => `${vacio !== undefined ? `<option value="">${esc(vacio)}</option>` : ''}${pares.map(([v, t]) => `<option value="${v}">${esc(t)}</option>`).join('')}`;
const fecha = (v, precision) => {
  if (!v) return '—';
  // B5c: un 'YYYY-MM-DD' (columna DATE) es un día, no medianoche UTC
  const d = typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T00:00:00`) : new Date(v);
  if (isNaN(d)) return '—';
  const dia = d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  return precision === 'dia' ? dia : `${dia} · ${d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`;
};
const estadoBadge = (e) => badge(NOMBRE_ESTADO[e] || e || '—', COLOR_ESTADO[e] || 'info');

export default {
  data: { filtros: { page: 1, limit: 25 }, unidades: [], pagination: null, resumen: null, productos: [], almacenes: [], edit: null },

  async render() {
    const pp = permisosPantalla('tractores', { crear: null });
    const arriba = `
      <div class="erp-aviso erp-aviso-info">
        Antes «Tractores». Aquí están <strong>todas las unidades con número de serie</strong>: tractores, implementos, drones, vehículos y maquinaria ligera.
        La unidad <strong>entra por Recepciones</strong> (o llegó del GPS); aquí se consulta y se corrige su ficha.
      </div>`;

    const consultar = `
      <div id="un_resumen"></div>
      <div class="card">
        <div class="card-header" style="flex-wrap:wrap;gap:.5rem;">
          <h2 class="card-title">Unidades</h2>
          <div style="display:flex;gap:.5rem;flex-wrap:wrap;align-items:center;">
            <input type="text" id="un_buscar" class="form-control" placeholder="Serie, modelo, motor, producto, cliente…" style="max-width:240px;" />
            <select id="un_tipo" class="form-control" style="max-width:170px;">${opciones(TIPOS, 'Todos los tipos')}</select>
            <select id="un_estado" class="form-control" style="max-width:170px;">${opciones(ESTADOS, 'Todos los estados')}</select>
            <select id="un_origen" class="form-control" style="max-width:190px;">${opciones(ORIGENES, 'Cualquier origen')}</select>
            <select id="un_almacen" class="form-control" style="max-width:190px;"><option value="">Todos los almacenes</option></select>
            <select id="un_gps" class="form-control" style="max-width:150px;"><option value="">Con o sin GPS</option><option value="con">Con GPS</option><option value="sin">Sin GPS</option></select>
            <label class="erp-check"><input type="checkbox" id="un_bajas" /> Ver dadas de baja</label>
          </div>
        </div>
        <div class="table-container">
          <table class="table">
            <thead><tr>
              <th>Serie</th><th>Tipo</th><th class="tbl-hide-mobile">Producto / modelo</th><th>Estado</th>
              <th class="tbl-hide-mobile">Almacén</th><th class="tbl-hide-mobile">Cliente · distribuidor</th><th class="tbl-hide-mobile">GPS</th>
              <th class="tbl-hide-mobile">Origen</th><th style="text-align:right;">Acciones</th>
            </tr></thead>
            <tbody id="unBody"><tr><td colspan="9" class="text-center"><div class="loading-spinner"></div></td></tr></tbody>
          </table>
        </div>
        <div id="un_paginas" style="display:flex;justify-content:space-between;align-items:center;padding:.6rem 0;font-size:var(--font-size-sm);"></div>
      </div>`;

    const formulario = `
      <div class="card erp-form-card editando" id="unFormCard">
        <div class="card-header"><h2 class="card-title" id="unFormTitulo">Ficha de la unidad</h2><span id="unFormOrigen" class="text-muted"></span></div>
        <form id="unForm" autocomplete="off">
          <div id="un_oc" class="erp-aviso erp-aviso-info" style="display:none;"></div>
          <div class="form-row">
            <div class="form-group"><label>Número de serie</label><input type="text" id="uf_serie" class="form-control" disabled />
              <div class="form-help">La serie se corrige desde Recepciones (Corregir NS), para que la cascada llegue a todo.</div></div>
            <div class="form-group" style="max-width:220px;"><label>Tipo *</label><select id="uf_tipo" class="form-control">${opciones(TIPOS)}</select></div>
            <div class="form-group" style="max-width:220px;"><label>Origen de la serie</label>
              <select id="uf_serie_origen" class="form-control"><option value="fabricante">De fábrica</option><option value="interna">Interna (AMX-…)</option></select></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Producto</label><select id="uf_producto" class="form-control"></select>
              <div class="form-help">Al elegir el producto se ajustan el modelo y la marca.</div></div>
            <div class="form-group" style="max-width:160px;"><label>Año modelo</label><input type="number" id="uf_anio" class="form-control" min="1950" max="2100" /></div>
            <div class="form-group" style="max-width:200px;"><label>Fecha de fabricación</label><input type="date" id="uf_fab" class="form-control" /></div>
          </div>
          <div class="form-row">
            <div class="form-group"><label>Serie del motor</label><input type="text" id="uf_motor" class="form-control" maxlength="50" style="text-transform:uppercase;" /></div>
            <div class="form-group" style="max-width:180px;"><label>Horas de motor</label><input type="number" id="uf_horas" class="form-control" min="0" step="0.01" /></div>
            <div class="form-group"><label>Almacén</label><select id="uf_almacen" class="form-control"></select></div>
            <div class="form-group" style="display:flex;align-items:flex-end;"><label class="erp-check"><input type="checkbox" id="uf_ocima" /> Certificación OCIMA</label></div>
          </div>
          <div class="erp-seccion">
            <h4>Estado</h4>
            <div class="form-row">
              <div class="form-group" style="max-width:240px;"><label>Estado</label><select id="uf_estado" class="form-control">${opciones(ESTADOS)}</select></div>
              <div class="form-group" id="uf_motivo_g" style="display:none;"><label>Motivo del cambio *</label>
                <input type="text" id="uf_motivo" class="form-control" maxlength="200" placeholder="Queda en la bitácora de la unidad" /></div>
            </div>
            <div class="form-help">El estado normalmente lo mueven los formatos (recepción, ensamble, liberación, entrega). Cambiarlo aquí deja el evento «Cambio de etapa» con tu motivo.</div>
          </div>
          <div class="erp-acciones-form">
            <button type="button" class="btn btn-secondary" id="btnUnCancelar">Cancelar</button>
            <button type="submit" class="btn btn-primary" id="btnUnGuardar">Guardar ficha</button>
          </div>
        </form>
      </div>`;

    return pestanasHtml({
      id: 'unid', titulo: 'Unidades', ...pp, arriba, consultar, formulario,
      avisoSoloEdicion: 'El alta de una unidad se hace en Recepciones. Aquí se corrige su ficha: elige una con el lápiz ✏️ en «Consultar».'
    });
  },

  async afterRender() {
    if (!permisos.canView('tractores')) {
      if (permisos.primeraRuta) { router.navigate(permisos.primeraRuta); return; }
      document.getElementById('content').innerHTML = '<div style="padding:2rem;text-align:center;"><h2>Acceso denegado</h2><p>No tienes permisos para acceder a este módulo.</p></div>';
      return;
    }
    window.unidadesView = this;
    this.data.edit = null;
    this.data.filtros = { page: 1, limit: 25 };
    this.tabs = new Pestanas('unid', { alAgregar: () => this.limpiar() });

    const q = new URLSearchParams(window.location.search);
    if (q.get('serie')) { this.data.filtros.search = q.get('serie'); const b = document.getElementById('un_buscar'); if (b) b.value = q.get('serie'); }

    const f = (id, campo) => document.getElementById(id)?.addEventListener('change', (e) => {
      this.data.filtros[campo] = e.target.type === 'checkbox' ? (e.target.checked ? '1' : '') : e.target.value;
      this.data.filtros.page = 1;
      this.cargarLista();
    });
    f('un_tipo', 'tipo'); f('un_estado', 'estado'); f('un_origen', 'alta_origen'); f('un_almacen', 'almacen_id'); f('un_gps', 'gps'); f('un_bajas', 'incluir_bajas');
    let t;
    document.getElementById('un_buscar')?.addEventListener('input', (e) => {
      clearTimeout(t);
      t = setTimeout(() => { this.data.filtros.search = e.target.value.trim(); this.data.filtros.page = 1; this.cargarLista(); }, 300);
    });
    document.getElementById('unForm')?.addEventListener('submit', (e) => { e.preventDefault(); this.guardar(); });
    document.getElementById('btnUnCancelar')?.addEventListener('click', () => { this.limpiar(); this.tabs.volver(); });
    document.getElementById('uf_estado')?.addEventListener('change', () => this.pintarMotivo());

    await Promise.all([this.cargarCatalogos(), this.cargarResumen(), this.cargarLista()]);
    if (q.get('ver')) this.verFicha(q.get('ver'));
  },

  async cargarCatalogos() {
    try {
      const [ra, rp] = await Promise.all([
        almacenService.listarActivos(),
        this.tabs.conFormulario ? catalogos.productos.listar({}) : Promise.resolve(null)
      ]);
      this.data.almacenes = ra?.data?.almacenes || [];
      const prods = rp?.data?.productos || (Array.isArray(rp?.data) ? rp.data : []);
      this.data.productos = prods.filter((p) => !p.deleted_at && p.activo !== 0);
    } catch (err) {
      toast.error(errMsg(err, 'No se pudieron cargar almacenes y productos'));
    }
    const alm = this.data.almacenes.map((a) => `<option value="${a.id}">${esc(a.codigo)} - ${esc(a.nombre)}</option>`).join('');
    const fa = document.getElementById('un_almacen');
    if (fa) fa.innerHTML = `<option value="">Todos los almacenes</option>${alm}`;
    const ua = document.getElementById('uf_almacen');
    if (ua) ua.innerHTML = `<option value="">— Sin asignar —</option>${alm}`;
    const up = document.getElementById('uf_producto');
    if (up) up.innerHTML = '<option value="">— Sin producto —</option>' +
      this.data.productos.map((p) => `<option value="${p.id}">${esc(p.clave)} · ${esc(p.descripcion || '')}</option>`).join('');
  },

  async cargarResumen() {
    const el = document.getElementById('un_resumen');
    try {
      const r = (await unidadesSvc.resumen())?.data;
      this.data.resumen = r;
      if (!el || !r) return;
      el.innerHTML = `
        <div class="erp-checks" style="margin-bottom:var(--spacing-md);font-size:var(--font-size-sm);">
          <span><strong>${r.total}</strong> unidades</span>
          ${r.por_tipo.map((x) => `<a href="#" onclick="window.unidadesView.filtrar('tipo','${x.tipo}');return false;">${x.n} ${esc((NOMBRE_TIPO[x.tipo] || x.tipo).toLowerCase())}</a>`).join('')}
          <span>${r.con_gps} con GPS</span>
          ${r.del_gps ? `<a href="#" onclick="window.unidadesView.filtrar('alta_origen','gps');return false;">${r.del_gps} entraron del GPS</a>` : ''}
          ${r.gps_sin_unidad ? `<a href="/admin/tracking" data-link class="badge badge-warning">${r.gps_sin_unidad} GPS sin unidad</a>` : ''}
        </div>`;
    } catch (err) {
      if (el) el.innerHTML = '';
    }
  },

  filtrar(campo, valor) {
    this.data.filtros[campo] = valor;
    this.data.filtros.page = 1;
    const ids = { tipo: 'un_tipo', alta_origen: 'un_origen' };
    const s = document.getElementById(ids[campo]);
    if (s) s.value = valor;
    this.cargarLista();
  },

  async cargarLista() {
    const body = document.getElementById('unBody');
    if (!body) return;
    try {
      const r = (await unidadesSvc.listar(this.data.filtros))?.data || {};
      this.data.unidades = r.unidades || [];
      this.data.pagination = r.pagination;
      const pe = this.tabs?.conFormulario && permisos.can('tractores', 'editar');
      const pb = permisos.canDelete('tractores');
      body.innerHTML = this.data.unidades.length ? this.data.unidades.map((u) => {
        const ns = esc(u.num_serie);
        const bloqueado = Number(u.recepciones_count) > 0 || !!u.oc_folio;
        return `
          <tr${u.deleted_at ? ' style="opacity:.55;"' : ''}>
            <td><strong>${ns}</strong>${u.serie_motor ? `<div class="text-muted" style="font-size:.75rem;">Motor ${esc(u.serie_motor)}</div>` : ''}</td>
            <td>${esc(NOMBRE_TIPO[u.tipo] || u.tipo)}</td>
            <td class="tbl-hide-mobile">${u.producto_clave ? `<span class="erp-chip">${esc(u.producto_clave)}</span><br>` : ''}${esc(u.modelo)} <span class="text-muted">· ${esc(u.marca_nombre || u.marca)}</span></td>
            <td>${u.deleted_at ? badge('Dada de baja', 'danger') : estadoBadge(u.estado)}</td>
            <td class="tbl-hide-mobile">${esc(u.almacen_nombre || '—')}</td>
            <td class="tbl-hide-mobile">${esc(u.cliente_nombre || '—')}${u.distribuidor_nombre ? `<div class="text-muted" style="font-size:.75rem;">Dist.: ${esc(u.distribuidor_nombre)}</div>` : ''}</td>
            <td class="tbl-hide-mobile">${u.gps_vehiculo_id ? `<a href="/admin/tracking/${u.gps_vehiculo_id}" data-link title="${esc(u.gps_nombre_activo || '')}">📡 ${esc(u.gps_placa || u.gps_nombre_activo || 'GPS')}</a>` : '<span class="text-muted">—</span>'}</td>
            <td class="tbl-hide-mobile">${u.alta_origen === 'gps' ? badge('GPS', 'warning') : `<span class="text-muted" style="font-size:.8rem;">${esc(NOMBRE_ORIGEN[u.alta_origen] || u.alta_origen)}</span>`}</td>
            <td style="text-align:right;white-space:nowrap;">
              <button class="btn btn-sm btn-secondary" title="Ver ficha y línea de tiempo" onclick="window.unidadesView.verFicha('${ns}')">👁</button>
              <button class="btn btn-sm btn-secondary" title="Historia completa" onclick="window.unidadesView.historia('${ns}')">📜</button>
              ${pe && !u.deleted_at ? `<button class="btn btn-sm btn-primary" title="Editar la ficha" onclick="window.unidadesView.editar('${ns}')">✏️</button>` : ''}
              ${pb && !u.deleted_at ? `<button class="btn btn-sm btn-danger"${bloqueado ? ' style="opacity:.5;"' : ''} title="Eliminar" onclick="window.unidadesView.eliminar('${ns}', ${Number(u.recepciones_count) > 0}, '${esc(u.oc_folio || '')}')">🗑</button>` : ''}
            </td>
          </tr>`;
      }).join('') : '<tr><td colspan="9" class="text-center text-muted">No hay unidades con esos filtros</td></tr>';
      this.pintarPaginas();
    } catch (err) {
      body.innerHTML = `<tr><td colspan="9" class="text-center" style="color:var(--danger);">${esc(errMsg(err, 'No se pudieron cargar las unidades'))}</td></tr>`;
    }
  },

  pintarPaginas() {
    const el = document.getElementById('un_paginas');
    const p = this.data.pagination;
    if (!el || !p) return;
    el.innerHTML = `
      <span class="text-muted">${p.total} unidades · página ${p.page} de ${Math.max(1, p.totalPages)}</span>
      <span style="display:flex;gap:.4rem;">
        <button class="btn btn-sm btn-secondary" ${p.page <= 1 ? 'disabled' : ''} onclick="window.unidadesView.pagina(${p.page - 1})">← Anterior</button>
        <button class="btn btn-sm btn-secondary" ${p.page >= p.totalPages ? 'disabled' : ''} onclick="window.unidadesView.pagina(${p.page + 1})">Siguiente →</button>
      </span>`;
  },

  pagina(n) { this.data.filtros.page = n; this.cargarLista(); },

  historia(ns) { router.navigate(`/tractores/${encodeURIComponent(ns)}/historia`); },

  async verFicha(ns) {
    try {
      const u = (await unidadesSvc.obtener(ns))?.data;
      if (!u) return;
      const fila = (k, v) => `<dt style="color:var(--gray-500);font-weight:600;">${esc(k)}</dt><dd style="margin:0;">${v}</dd>`;
      const eventos = (u.linea_tiempo || []).map((e) => `
        <li style="margin-bottom:.45rem;${e.anulado ? 'text-decoration:line-through;opacity:.6;' : ''}">
          <strong>${esc(e.etiqueta || e.evento)}</strong> <span class="text-muted">· ${fecha(e.ocurrido_at, e.ocurrido_precision)}${e.ocurrido_precision === 'dia' ? ' (día)' : ''}</span>
          ${e.dias_desde_anterior != null ? `<span class="text-muted"> · +${e.dias_desde_anterior} d</span>` : ''}
          ${e.nota ? `<div class="text-muted" style="font-size:.8rem;">${esc(e.nota)}</div>` : ''}
          ${e.usuario_nombre ? `<div class="text-muted" style="font-size:.75rem;">${esc(e.usuario_nombre)}</div>` : ''}
        </li>`).join('');
      const html = `
        <dl style="display:grid;grid-template-columns:minmax(120px,170px) 1fr;gap:.3rem .9rem;margin-bottom:1rem;font-size:.9rem;">
          ${fila('Serie', `<strong>${esc(u.num_serie)}</strong>`)}
          ${fila('Tipo', esc(NOMBRE_TIPO[u.tipo] || u.tipo))}
          ${fila('Estado', estadoBadge(u.estado))}
          ${fila('Producto', u.producto_clave ? `${esc(u.producto_clave)} · ${esc(u.producto_descripcion || '')}` : '<span class="text-muted">Sin producto</span>')}
          ${fila('Modelo · marca', `${esc(u.modelo)} · ${esc(u.marca_nombre || u.marca)}`)}
          ${fila('Serie de motor', esc(u.serie_motor || '—'))}
          ${fila('Pedimento', u.pedimento_id ? esc(u.pedimento_id) : '<span class="text-muted">Pendiente (catálogo de pedimentos por confirmar)</span>')}
          ${fila('Almacén', esc(u.almacen_nombre || '—'))}
          ${fila('Cliente', esc(u.cliente_nombre || '—'))}
          ${fila('Distribuidor', esc(u.distribuidor_nombre || '—'))}
          ${fila('GPS', u.gps_vehiculo_id ? `<a href="/admin/tracking/${u.gps_vehiculo_id}" data-link onclick="window.modal&&window.modal.close&&window.modal.close()">📡 ${esc(u.gps_nombre_activo || '')} ${u.gps_placa ? `(placa ${esc(u.gps_placa)})` : ''}</a>` : '<span class="text-muted">Sin GPS ligado</span>')}
          ${fila('Entró por', esc(NOMBRE_ORIGEN[u.alta_origen] || u.alta_origen))}
          ${u.oc_folio ? fila('Orden de compra', esc(u.oc_folio)) : ''}
        </dl>
        <h4 style="font-size:.85rem;text-transform:uppercase;color:var(--gray-600);margin-bottom:.5rem;">Línea de tiempo</h4>
        ${eventos ? `<ol style="padding-left:1.1rem;max-height:260px;overflow:auto;">${eventos}</ol>` : '<p class="text-muted">Sin eventos en la bitácora.</p>'}`;
      const botones = [{ text: 'Historia completa', class: 'btn-secondary', onClick: () => this.historia(u.num_serie) }];
      if (this.tabs?.conFormulario && permisos.can('tractores', 'editar') && !u.deleted_at) {
        botones.push({ text: 'Editar ficha', class: 'btn-primary', onClick: () => this.editar(u.num_serie) });
      }
      botones.push({ text: 'Cerrar', class: 'btn-secondary' });
      modal.open(`Unidad ${u.num_serie}`, html, botones);
    } catch (err) {
      toast.error(errMsg(err, 'No se pudo abrir la ficha'));
    }
  },

  limpiar() {
    this.data.edit = null;
    document.getElementById('unForm')?.reset();
    const oc = document.getElementById('un_oc');
    if (oc) oc.style.display = 'none';
    this.pintarMotivo();
  },

  async editar(ns) {
    try {
      const u = (await unidadesSvc.obtener(ns))?.data;
      if (!u) return;
      this.data.edit = u;
      const pon = (id, v) => { const el = document.getElementById(id); if (!el) return; if (el.type === 'checkbox') el.checked = !!v; else el.value = v ?? ''; };
      pon('uf_serie', u.num_serie); pon('uf_tipo', u.tipo); pon('uf_serie_origen', u.serie_origen || 'fabricante');
      pon('uf_producto', u.producto_id || ''); pon('uf_anio', u.anio_modelo); pon('uf_fab', u.fecha_fabricacion ? String(u.fecha_fabricacion).slice(0, 10) : '');
      pon('uf_motor', u.serie_motor); pon('uf_horas', Number(u.horas_motor) || 0); pon('uf_almacen', u.almacen_id || '');
      pon('uf_ocima', u.tiene_certificacion_ocima); pon('uf_estado', u.estado); pon('uf_motivo', '');
      const prod = document.getElementById('uf_producto');
      if (prod) prod.disabled = !!u.oc_folio;
      const oc = document.getElementById('un_oc');
      if (oc) {
        oc.style.display = u.oc_folio ? '' : 'none';
        oc.textContent = u.oc_folio ? `Viene de la ${u.oc_folio}: su producto es el del pedido y no se cambia aquí.` : '';
      }
      const or = document.getElementById('unFormOrigen');
      if (or) or.textContent = `Entró por: ${NOMBRE_ORIGEN[u.alta_origen] || u.alta_origen}`;
      this.pintarMotivo();
      modal.close?.();
      this.tabs.abrirFormulario({ editando: true, etiqueta: u.num_serie });
    } catch (err) {
      toast.error(errMsg(err, 'No se pudo cargar la unidad'));
    }
  },

  pintarMotivo() {
    const g = document.getElementById('uf_motivo_g');
    const e = document.getElementById('uf_estado')?.value;
    if (g) g.style.display = this.data.edit && e && e !== this.data.edit.estado ? '' : 'none';
  },

  async guardar() {
    const u = this.data.edit;
    if (!u) { toast.error('Elige una unidad con el lápiz ✏️'); return; }
    const body = {
      tipo: val('uf_tipo'),
      serie_origen: val('uf_serie_origen'),
      anio_modelo: val('uf_anio') === '' ? null : Number(val('uf_anio')),
      fecha_fabricacion: val('uf_fab') || null,
      serie_motor: val('uf_motor') || null,
      horas_motor: val('uf_horas') === '' ? 0 : Number(val('uf_horas')),
      almacen_id: val('uf_almacen') ? Number(val('uf_almacen')) : null,
      tiene_certificacion_ocima: val('uf_ocima')
    };
    if (!u.oc_folio) body.producto_id = val('uf_producto') ? Number(val('uf_producto')) : null;
    const estado = val('uf_estado');
    if (estado !== u.estado) { body.estado = estado; body.motivo_estado = val('uf_motivo'); }
    const btn = document.getElementById('btnUnGuardar');
    if (btn) btn.disabled = true;
    try {
      await unidadesSvc.actualizar(u.num_serie, body);
      toast.success(`Unidad ${u.num_serie} actualizada`);
      this.limpiar();
      this.tabs.volver();
      await Promise.all([this.cargarLista(), this.cargarResumen()]);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      if (btn) btn.disabled = false;
    }
  },

  eliminar(ns, tieneRecepciones, ocFolio) {
    if (ocFolio) { modal.alert('No se puede eliminar', `La unidad <strong>${esc(ns)}</strong> proviene de la <strong>${esc(ocFolio)}</strong>.`); return; }
    if (tieneRecepciones) { modal.alert('No se puede eliminar', `La unidad <strong>${esc(ns)}</strong> tiene recepciones asociadas.`); return; }
    modal.confirm('Confirmar eliminación', `¿Eliminar la unidad <strong>${esc(ns)}</strong>?`, async () => {
      try {
        await tractorService.delete(ns);
        toast.success('Unidad eliminada');
        await Promise.all([this.cargarLista(), this.cargarResumen()]);
      } catch (err) {
        toast.error(errMsg(err, 'No se pudo eliminar'));
      }
    });
  }
};
